import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Printer, ArrowLeft, Soup, CakeSlice, Sandwich } from "lucide-react";
import { fetchCountries } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { KIND_LABEL, pickText } from "@/lib/recipe";
import { RecipeMeta, RecipeBody, VideoRecipeLink } from "@/components/RecipeDialog";
import EbookCover from "@/components/ebook/EbookCover";
import HowToUse from "@/components/ebook/HowToUse";
import LiveFood from "@/components/LiveFood";
import Paywall from "@/components/ebook/Paywall";
import useEbookAccess from "@/lib/useEbookAccess";
import ReceiptPrompt from "@/components/ebook/ReceiptPrompt";

const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const KINDS = ["dish", "dessert", "street"];
const shortName = (c, lang) => c.short_names?.[lang] || c.names[lang] || c.names.en;

export default function Cookbook() {
  const { lang, t } = useLang();
  const { hash } = useLocation();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const list = useMemo(() => countries.filter((c) => c.travel_guide?.cuisine?.dish?.recipe)
    .sort((a, b) => shortName(a, lang).localeCompare(shortName(b, lang), lang)), [countries, lang]);
  const total = list.reduce((n, c) => n + KINDS.filter((k) => c.travel_guide.cuisine[k]?.recipe).length, 0);
  const { unlocked, buy, buying, viaPlay, unlockWith, priceLabel, bundleLabel, receiptToken, closeReceipt } = useEbookAccess("ebook_cookbook");
  const product = "ebook_cookbook";

  useEffect(() => {
    document.title = `${t("cookbook_title")} — ${t("app_title")}`;
    return () => { document.title = "Atlante del Mondo Arabo | أطلس العالم العربي"; };
  }, [t]);

  useEffect(() => {
    if (hash && list.length) document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [hash, list.length]);

  if (!list.length) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">…</div>;
  const onPrint = () => { if (!unlocked) return buy(product); trackEvent("print_cookbook", { lang, recipes: total }); window.print(); };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 print:py-0 print:px-0 print:max-w-none" data-testid="cookbook">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ochre hover:underline" data-testid="cookbook-back"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("back")}</Link>
        <button onClick={onPrint} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-sand text-sm hover:bg-ochre transition-colors" data-testid="cookbook-print-button"><Printer className="w-4 h-4" /> {unlocked ? t("print_pdf") : t("paywall_buy").replace("{price}", priceLabel)}</button>
      </div>
      <ReceiptPrompt token={receiptToken} onClose={closeReceipt} />

      <EbookCover kicker={t("app_title")} title={t("cookbook_title")} subtitle={t("cookbook_subtitle").replace("{n}", total).replace("{c}", list.length)} note={t("ebook_cover_note")} countries={list} testid="cookbook-cover" accent="#C8553D">
        <CoverDishes list={list} />
      </EbookCover>
      <h1 className="sr-only" data-testid="cookbook-title">{t("cookbook_title")}</h1>
      <HowToUse variant="cookbook" />
      <CookbookIndex list={list} lang={lang} t={t} />

      {(unlocked ? list : list.slice(0, 1)).map((c) => KINDS.map((k) => c.travel_guide.cuisine[k]?.recipe && (
        <RecipePage key={`${c.code}-${k}`} country={c} kind={k} food={c.travel_guide.cuisine[k]} lang={lang} t={t} />
      )))}
      {!unlocked && list[1] && (
        <Paywall product="ebook_cookbook" buy={buy} buying={buying} viaPlay={viaPlay} unlockWith={unlockWith} remaining={total - 3} priceLabel={priceLabel} bundleLabel={bundleLabel}>

          <RecipePage country={list[1]} kind="dish" food={list[1].travel_guide.cuisine.dish} lang={lang} t={t} />
        </Paywall>
      )}

      {unlocked && <section className="break-before-page pt-2 text-center space-y-4" data-testid="cookbook-end">
        <div className="text-[10px] uppercase tracking-[0.3em] text-ochre font-semibold">{t("app_title")}</div>
        <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink">{t("cookbook_end_title")}</h2>
        <p className="text-base leading-relaxed text-ink/85 max-w-xl mx-auto">{t("cookbook_end_text").replace("{n}", total).replace("{c}", list.length)}</p>
        <div className="flex flex-wrap justify-center gap-1.5 max-w-md mx-auto pt-2">{list.map((c) => <img key={c.code} src={c.flag_url} alt="" className="w-8 h-[22px] object-cover rounded-sm border border-stoneLine" />)}</div>
      </section>}

      <footer className="mt-8 pt-3 border-t border-stoneLine text-[10px] text-muted-foreground flex justify-between">
        <span>{t("photo_wiki")}</span>
        <span dir="ltr">{window.location.origin}/ricettario</span>
      </footer>
    </div>
  );
}

function CoverDishes({ list }) {
  const picks = [["TN", "dish"], ["MA", "dessert"], ["EG", "street"], ["LB", "dish"], ["SA", "dessert"], ["JO", "street"]]
    .map(([code, k]) => [list.find((c) => c.code === code)?.travel_guide?.cuisine?.[k], k]).filter(([f]) => f?.image);
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="cookbook-cover-dishes">
      {picks.map(([f, k], i) => <LiveFood key={i} food={f} kind={k} alt="" className="rounded-xl border-2 border-sand shadow-md aspect-square" />)}
    </div>
  );
}

function CookbookIndex({ list, lang, t }) {
  return (
    <nav className="mb-10 break-after-page" data-testid="cookbook-index">
      <h2 className="text-[10px] uppercase tracking-[0.18em] text-ochre font-semibold mb-3">{t("cookbook_index")}</h2>
      <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
        {list.map((c) => (
          <li key={c.code} className="flex items-start gap-2.5 text-sm" data-testid={`cookbook-index-${c.code}`}>
            <img src={c.flag_url} alt="" className="w-6 h-4 object-cover rounded-sm border border-stoneLine mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold text-ink">{shortName(c, lang)}</div>
              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                {KINDS.map((k) => c.travel_guide.cuisine[k]?.recipe && (
                  <a key={k} href={`#${c.code}-${k}`} className="hover:text-ochre hover:underline print:no-underline">{pickText(c.travel_guide.cuisine[k].name, lang)}</a>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function RecipePage({ country, kind, food, lang, t }) {
  const name = pickText(food.name, lang);
  const Icon = kind === "dessert" ? CakeSlice : kind === "street" ? Sandwich : Soup;
  return (
    <article id={`${country.code}-${kind}`} className="break-before-page pt-2 mb-12 print:mb-0 scroll-mt-24" data-testid={`cookbook-recipe-${country.code}-${kind}`}>
      <header className="flex items-center gap-4 border-b border-stoneLine pb-4 mb-4">
        <img src={country.flag_url} alt="" className="w-14 h-9 object-cover rounded-md border border-stoneLine shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-ochre font-semibold inline-flex items-center gap-1.5"><Icon className="w-3 h-3" /> {country.names[lang] || country.names.en} · {t(KIND_LABEL[kind])}</div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-ink leading-tight">{name}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{pickText(food.desc, lang)}</p>
        </div>
        <VideoRecipeLink food={food} code={country.code} kind={kind} className="text-xs px-3 py-1.5 print:hidden shrink-0" />
      </header>
      {food.image && <img src={imgSrc(food.image)} alt={name} loading="lazy" className="w-full h-48 object-cover rounded-xl border border-stoneLine mb-4" />}
      <div className="mb-4"><RecipeMeta recipe={food.recipe} /></div>
      <RecipeBody recipe={food.recipe} lang={lang} />
    </article>
  );
}
